import { useForm } from '@inertiajs/react'
import { useEffect, useState, type FormEvent } from 'react'
import { AlertTriangle, CheckCircle2, Lightbulb, Plus, Trash2, Upload } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { TextArea } from '@/components/ui/TextArea'
import { Label } from '@/components/ui/Label'
import { Select } from '@/components/ui/Select'
import { Button, buttonClasses } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { FieldError } from '@/components/feedback/FieldError'
import type { Breadcrumb } from '@/types/shared'

type PlayerVideoValue = {
  id?: string
  url: string
  position?: number | null
}

type PlayerFormValues = {
  name: string | null
  prefectureId: number | null
  birthday: string | null
  height: number | null
  fatherHeight: number | null
  motherHeight: number | null
  fiftyMeterTime: number | null
  boarding: boolean
  comment: string | null
  dominantFoot: string | null
  positionIds: string[]
  profileApprovalStatus: string
  videos: PlayerVideoValue[]
}

type Options = {
  positions: Array<{ id: string; name: string }>
  prefectures: Array<{ code: number; name: string }>
  dominantFoots: Array<{ value: string; label: string }>
}

type ProfileEditProps = {
  player: PlayerFormValues
  latestRejectedRequest?: {
    id: string
    rejectionReason?: string | null
    reviewedAt?: string | null
  } | null
  options: Options
  breadcrumbs: Breadcrumb[]
}

type FormVideo = {
  id?: string
  tempId: string
  url: string
}

const randomId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2, 10)

const toFormVideos = (videos: PlayerVideoValue[]): FormVideo[] => {
  if (!videos || videos.length === 0) {
    return [{ tempId: randomId(), url: '' }]
  }

  return [...videos]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((video) => ({
      id: video.id,
      tempId: video.id ?? randomId(),
      url: video.url ?? '',
    }))
}

export default function Edit({ player, latestRejectedRequest, options, breadcrumbs }: ProfileEditProps) {
  const { data, setData, errors, processing, transform, put } = useForm({
    name: player.name ?? '',
    prefecture_id: player.prefectureId?.toString() ?? '',
    birthday: player.birthday ?? '',
    height: player.height?.toString() ?? '',
    father_height: player.fatherHeight?.toString() ?? '',
    mother_height: player.motherHeight?.toString() ?? '',
    fifty_meter_time: player.fiftyMeterTime?.toString() ?? '',
    boarding: player.boarding ?? false,
    comment: player.comment ?? '',
    dominant_foot: player.dominantFoot ?? '',
    position_ids: player.positionIds.map(String),
  })

  const [videos, setVideos] = useState<FormVideo[]>(() => toFormVideos(player.videos))
  const [removedVideoIds, setRemovedVideoIds] = useState<string[]>([])
  const isApprovedProfile = player.profileApprovalStatus === 'approved'

  const videosSignature = JSON.stringify(
    (player.videos || []).map((video) => [video.id, video.url, video.position])
  )

  useEffect(() => {
    setVideos(toFormVideos(player.videos))
    setRemovedVideoIds([])
  }, [videosSignature])

  const updateField = <K extends keyof typeof data>(field: K, value: (typeof data)[K]) =>
    setData((previous) => ({
      ...previous,
      [field]: value,
    }))

  const togglePosition = (id: string) => {
    const current = data.position_ids
    if (current.includes(id)) {
      updateField('position_ids', current.filter((pid) => pid !== id))
    } else {
      updateField('position_ids', [...current, id])
    }
  }

  const handleVideoChange = (tempId: string, url: string) => {
    setVideos((prev) =>
      prev.map((video) =>
        video.tempId === tempId
          ? {
              ...video,
              url,
            }
          : video
      )
    )
  }

  const addVideo = () => {
    setVideos((prev) => [...prev, { tempId: randomId(), url: '' }])
  }

  const removeVideo = (tempId: string) => {
    setVideos((prev) => {
      const target = prev.find((video) => video.tempId === tempId)
      if (!target) return prev

      if (target.id) {
        setRemovedVideoIds((ids) => (ids.includes(target.id!) ? ids : [...ids, target.id!]))
      }

      const next = prev.filter((video) => video.tempId !== tempId)
      return next.length > 0 ? next : [{ tempId: randomId(), url: '' }]
    })
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const trimmed = videos.map((video) => ({ ...video, url: video.url.trim() }))

    const keepPayload = trimmed
      .filter((video) => video.url.length > 0)
      .map((video, index) => ({
        id: video.id,
        url: video.url,
        position: index,
      }))

    const destroySet = new Set(removedVideoIds)

    trimmed.forEach((video) => {
      if (video.id && video.url.length === 0) {
        destroySet.add(video.id)
      }
    })

    const keepIds = new Set(keepPayload.map((video) => video.id).filter(Boolean) as string[])
    const destroyPayload = Array.from(destroySet)
      .filter((id) => !keepIds.has(id))
      .map((id) => ({ id, _destroy: true }))

    transform((current) => ({
      player: {
        ...current,
        player_videos_attributes: [...keepPayload, ...destroyPayload],
      },
    }))

    put('/player/profile', {
      preserveScroll: true,
      onSuccess: () => {
        setRemovedVideoIds([])
      },
      onFinish: () => {
        transform((current) => current)
      },
    })
  }

  return (
    <AppShell>
      <PageHeader
        title="プロフィール編集"
        description="スカウト担当者が最初に確認する情報です。最新の状態に保ち、あなたの強みを伝えましょう。"
        breadcrumbs={breadcrumbs}
        actions={
          <Badge variant={statusBadgeVariant(player.profileApprovalStatus)}>
            {approvalLabel(player.profileApprovalStatus)}
          </Badge>
        }
      />

      <form onSubmit={submit} className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,2fr)_1fr]">
        <Card className="space-y-8">
          {isApprovedProfile ? (
            <div className="flex items-start gap-3 rounded-xl border border-amber-300/40 bg-amber-300/10 px-4 py-3 text-amber-50">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-200" />
              <p className="text-sm font-medium">
                保存するとプロフィールが一時非公開になります。チームコーチの再承認が必要です。
              </p>
            </div>
          ) : null}
          {latestRejectedRequest ? (
            <div className="flex items-start gap-3 rounded-xl border border-amber-300/40 bg-amber-300/10 px-4 py-3 text-amber-50">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-200" />
              <div className="space-y-1">
                <p className="text-sm font-semibold">プロフィールが差し戻されました</p>
                <p className="text-sm text-amber-50/80 whitespace-pre-wrap">
                  {latestRejectedRequest.rejectionReason || 'チームコーチからプロフィールへの追記が必要と判断されました。'}
                </p>
              </div>
            </div>
          ) : null}

          <section className="space-y-6">
            <h2 className="text-xl font-semibold text-white">基本情報</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <Label htmlFor="name">氏名</Label>
                <Input
                  id="name"
                  value={data.name}
                  onChange={(event) => updateField('name', event.target.value)}
                  placeholder="山田 太郎"
                />
                <FieldError className="mt-1" message={errors.name} />
              </div>
              <div>
                <Label htmlFor="prefecture">都道府県</Label>
                <Select
                  id="prefecture"
                  value={data.prefecture_id}
                  onChange={(event) => updateField('prefecture_id', event.target.value)}
                >
                  <option value="">選択してください</option>
                  {options.prefectures.map((prefecture) => (
                    <option key={prefecture.code} value={prefecture.code}>
                      {prefecture.name}
                    </option>
                  ))}
                </Select>
                <FieldError className="mt-1" message={errors.prefecture_id} />
              </div>
              <div>
                <Label htmlFor="birthday">生年月日</Label>
                <Input
                  id="birthday"
                  type="date"
                  value={data.birthday}
                  onChange={(event) => updateField('birthday', event.target.value)}
                />
                <FieldError className="mt-1" message={errors.birthday} />
              </div>
              <div>
                <Label htmlFor="dominantFoot">利き足</Label>
                <Select
                  id="dominantFoot"
                  value={data.dominant_foot}
                  onChange={(event) => updateField('dominant_foot', event.target.value)}
                >
                  <option value="">選択してください</option>
                  {options.dominantFoots.map((foot) => (
                    <option key={foot.value} value={foot.value}>
                      {foot.label}
                    </option>
                  ))}
                </Select>
                <FieldError className="mt-1" message={errors.dominant_foot} />
              </div>
            </div>
          </section>

          <section className="space-y-6">
            <h2 className="text-xl font-semibold text-white">フィジカルデータ</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <div>
                <Label htmlFor="height">身長 (cm)</Label>
                <Input
                  id="height"
                  type="number"
                  inputMode="decimal"
                  value={data.height}
                  onChange={(event) => updateField('height', event.target.value)}
                  placeholder="175"
                />
                <FieldError className="mt-1" message={errors.height} />
              </div>
              <div>
                <Label htmlFor="fatherHeight">父親の身長 (cm)</Label>
                <Input
                  id="fatherHeight"
                  type="number"
                  inputMode="decimal"
                  value={data.father_height}
                  onChange={(event) => updateField('father_height', event.target.value)}
                  placeholder="180"
                />
                <FieldError className="mt-1" message={errors.father_height} />
              </div>
              <div>
                <Label htmlFor="motherHeight">母親の身長 (cm)</Label>
                <Input
                  id="motherHeight"
                  type="number"
                  inputMode="decimal"
                  value={data.mother_height}
                  onChange={(event) => updateField('mother_height', event.target.value)}
                  placeholder="165"
                />
                <FieldError className="mt-1" message={errors.mother_height} />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <Label htmlFor="fiftyMeterTime">50m走タイム (秒)</Label>
                <Input
                  id="fiftyMeterTime"
                  type="number"
                  inputMode="decimal"
                  value={data.fifty_meter_time}
                  onChange={(event) => updateField('fifty_meter_time', event.target.value)}
                  placeholder="6.2"
                />
                <FieldError className="mt-1" message={errors.fifty_meter_time} />
              </div>
              <div>
                <Label>下宿可否</Label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => updateField('boarding', !data.boarding)}
                    className={buttonClasses({ variant: data.boarding ? 'primary' : 'secondary', size: 'sm' })}
                  >
                    {data.boarding ? '下宿可能' : '下宿不可'}
                  </button>
                  <span className="text-xs text-white/50">遠方校への進学希望かどうか</span>
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-6">
            <h2 className="text-xl font-semibold text-white">ポジション</h2>
            <div className="flex flex-wrap gap-3">
              {options.positions.map((position) => {
                const selected = data.position_ids.includes(position.id)
                return (
                  <button
                    key={position.id}
                    type="button"
                    onClick={() => togglePosition(position.id)}
                    className={buttonClasses({ variant: selected ? 'primary' : 'secondary', size: 'sm' })}
                  >
                    {position.name}
                  </button>
                )
              })}
            </div>
            <FieldError className="mt-1" message={errors.position_ids} />
          </section>

          <section className="space-y-6">
            <h2 className="text-xl font-semibold text-white">自己PR</h2>
            <TextArea
              rows={6}
              value={data.comment}
              onChange={(event) => updateField('comment', event.target.value)}
              placeholder="プレースタイルやチームでの役割、これまでの受賞歴などを記入してください。"
            />
            <FieldError className="mt-1" message={errors.comment} />
          </section>

          <section className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-white">動画リンク</h2>
                <p className="mt-1 text-xs text-white/50">YouTubeの限定公開リンクなど、実際のプレー動画を登録してください。</p>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={addVideo} className="flex items-center gap-2 text-white/70">
                <Plus className="h-4 w-4" />
                動画を追加
              </Button>
            </div>
            <div className="space-y-4">
              {videos.map((video, index) => {
                // errors["player_videos[0].url"] のようなキーからエラーを取得
                const videoError = (errors as Record<string, string>)[`player_videos[${index}].url`]
                return (
                  <div key={video.id ?? video.tempId} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="flex items-center justify-between gap-4">
                      <Label htmlFor={`video-${video.tempId}`} className="text-sm text-white">
                        動画URL {index + 1}
                      </Label>
                      <button
                        type="button"
                        onClick={() => removeVideo(video.tempId)}
                        className="flex items-center gap-1 text-xs text-white/50 transition hover:text-white"
                      >
                        <Trash2 className="h-4 w-4" />
                        削除
                      </button>
                    </div>
                    <Input
                      id={`video-${video.tempId}`}
                      type="url"
                      placeholder="https://www.youtube.com/watch?v=..."
                      value={video.url}
                      onChange={(event) => handleVideoChange(video.tempId, event.target.value)}
                    />
                    {videoError ? <FieldError className="mt-1" message={videoError} /> : null}
                  </div>
                )
              })}
            </div>
          </section>

          <div className="flex justify-end gap-3">
            <a href="/player" className={buttonClasses({ variant: 'ghost' })}>
              キャンセル
            </a>
            <Button type="submit" loading={processing}>
              <Upload className="mr-2 h-4 w-4" />保存する
            </Button>
          </div>
        </Card>

        <aside className="space-y-6">
          <Card variant="muted" className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-500/30 text-brand-100">
                <Lightbulb className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-white">入力のコツ</h3>
                <p className="text-xs text-white/60">数字は正確に、動画は最新シーズンのものを登録すると反応率が高まります。</p>
              </div>
            </div>
            <ul className="space-y-3 text-sm text-white/70">
              <li>・利き足、50m走タイムは最新の計測値を入力してください。</li>
              <li>・自己PRでは得意なプレーと期待される役割をセットで伝えましょう。</li>
              <li>・動画は1本以上登録すると、スカウト閲覧率が平均で1.8倍に。</li>
            </ul>
          </Card>

          <Card className="space-y-3">
            <h3 className="font-semibold text-white">現在のステータス</h3>
            <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/70">
              <CheckCircle2 className="h-5 w-5 text-brand-300" />
              <span>{statusMessage(player.profileApprovalStatus)}</span>
            </div>
            <p className="text-xs text-white/50">更新すると再度承認プロセスが走ります。内容に間違いがないか確認してください。</p>
          </Card>
        </aside>
      </form>
    </AppShell>
  )
}

function statusBadgeVariant(status: string) {
  switch (status) {
    case 'approved':
      return 'success' as const
    case 'pending':
      return 'warning' as const
    default:
      return 'outline' as const
  }
}

function approvalLabel(status: string) {
  switch (status) {
    case 'approved':
      return '承認済み'
    case 'pending':
      return '承認待ち'
    default:
      return '未提出'
  }
}

function statusMessage(status: string) {
  switch (status) {
    case 'approved':
      return '承認済み：高校コーチが閲覧できる状態です。'
    case 'pending':
      return '承認待ち：チームスタッフによる確認中です。'
    default:
      return '未提出：入力後「保存する」を押すと承認に進みます。'
  }
}
